import type { RoundBooking } from "@bt/domain";
import { useSyncExternalStore } from "react";
import { garage } from "@/lib/garage";

/*
 * The picking round (ADR 0018 › Booking to a work order): the answer to "Book to
 * a work order?" holds for every further Take until the round is finished. It lives in the tab's
 * session storage, so a reload keeps it and closing the tab ends it; without session storage it
 * lasts as long as the page. Leaving the scanner ends it too (`endRound`, from the root layout).
 */

/** What the round books to: a work order, or none. Not asked yet while the round has none. */
export type RoundTarget = { workOrderId: string; title: string } | { workOrderId: null };

export interface Round {
  target: RoundTarget | null;
  bookings: RoundBooking[];
}

interface State {
  round: Round;
  /** The round just finished, for its summary until the next scan. */
  finished: Round | null;
}

const KEY = `bt-scanner-round:${garage ?? ""}`;
const EMPTY: Round = { target: null, bookings: [] };

function stored(): Round {
  try {
    const value = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Round | null;
    if (value && Array.isArray(value.bookings)) return value;
  } catch {
    // Not available, or not ours: a new round.
  }
  return EMPTY;
}

let state: State = { round: stored(), finished: null };
const listeners = new Set<() => void>();

function set(next: Partial<State>) {
  state = { ...state, ...next };
  try {
    const { round } = state;
    if (round.target || round.bookings.length) sessionStorage.setItem(KEY, JSON.stringify(round));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Kept in memory only.
  }
  for (const listener of listeners) listener();
}

/** Whether a round is going on: its question was answered, or something was booked. */
export function roundOpen(round: Round): boolean {
  return round.target !== null || round.bookings.length > 0;
}

export function setRoundTarget(target: RoundTarget): void {
  set({ round: { ...state.round, target } });
}

export function addRoundBooking(booking: RoundBooking): void {
  set({ round: { ...state.round, bookings: [...state.round.bookings, booking] } });
}

/** Undo: the booking leaves the round as it leaves the history. */
export function removeRoundBooking(movementId: string): void {
  set({
    round: {
      ...state.round,
      bookings: state.round.bookings.filter((b) => b.movementId !== movementId),
    },
  });
}

/** Finish round: its summary shows, and the next Take asks again. */
export function finishRound(): void {
  set({ round: EMPTY, finished: state.round });
}

export function closeSummary(): void {
  set({ finished: null });
}

/** The scanner was left: the round ends without a summary. */
export function endRound(): void {
  if (roundOpen(state.round) || state.finished) set({ round: EMPTY, finished: null });
}

export function useRound(): State {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
